import { keyframes } from '@emotion/react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LaunchIcon from '@mui/icons-material/Launch';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loading } from '@/core/components';
import { useAuth } from '@/core/hooks/useAuth';
import { getClaimHistory } from '@/core/services/claim';
import { getExplorerUrl } from '@/core/services/event-admin';
import type { Claim } from '@/core/types';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

function formatDate(timestamp: { toDate: () => Date } | null): string {
  if (!timestamp) return '-';
  const date = timestamp.toDate();
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusConfig(status: string): { label: string; color: 'success' | 'info' | 'error' | 'default' } {
  const configs: Record<string, { label: string; color: 'success' | 'info' | 'error' | 'default' }> = {
    ready: { label: '準備中', color: 'info' },
    'video-queued': { label: '処理中', color: 'info' },
    'video-processing': { label: '処理中', color: 'info' },
    'video-done': { label: '処理中', color: 'info' },
    'mint-queued': { label: '処理中', color: 'info' },
    'mint-processing': { label: '処理中', color: 'info' },
    'mint-pending': { label: '処理中', color: 'info' },
    completed: { label: '完了', color: 'success' },
    'video-failed': { label: '失敗', color: 'error' },
    'mint-failed': { label: '失敗', color: 'error' },
    'permanently-failed': { label: '失敗', color: 'error' },
  };
  return configs[status] || { label: status, color: 'default' };
}

export function HistoryPage() {
  const { raceAlias } = useParams<{ raceAlias: string }>();
  const { user, loading: authLoading } = useAuth();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const fetchHistory = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const history = await getClaimHistory(user.uid);
      setClaims(history);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setError('履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchHistory();
    }
  }, [authLoading, user, fetchHistory]);

  if (authLoading) {
    return <Loading />;
  }

  if (!user) {
    return (
      <Alert severity="warning" sx={{ animation: `${fadeInUp} 0.6s ease-out` }}>
        ログインが必要です
      </Alert>
    );
  }

  return (
    <Box>
      {/* タイトルエリア */}
      <Box
        sx={{
          textAlign: 'center',
          mt: { xs: 2, sm: 5 },
          mb: { xs: 3, sm: 5 },
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            letterSpacing: '0.2em',
            color: 'text.secondary',
            mb: 1,
            fontSize: '0.7rem',
          }}
        >
          YOUR ACHIEVEMENTS
        </Typography>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 800,
            background: gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          受け取り履歴
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Loading />
      ) : claims.length === 0 ? (
        /* 空状態 */
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
          }}
        >
          <MilitaryTechIcon
            sx={{
              fontSize: 64,
              color: 'text.disabled',
              mb: 2,
            }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            まだ履歴がありません
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: raceAlias ? 3 : 0 }}>
            完走証を受け取ると表示されます
          </Typography>
          {raceAlias && (
            <Button
              variant="outlined"
              href={`/${raceAlias}/claim`}
              sx={{
                position: 'relative',
                fontWeight: 600,
                border: 'none',
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 'inherit',
                  padding: '2px',
                  background: gradient,
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  pointerEvents: 'none',
                },
                '&:hover': {
                  background: theme.palette.background.paper,
                },
              }}
            >
              完走証を受け取る
            </Button>
          )}
        </Box>
      ) : (
        /* 履歴カード一覧 */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {claims.map((claim, index) => {
            const statusConfig = getStatusConfig(claim.status);
            return (
              <Paper
                key={claim.id}
                sx={{
                  p: 2,
                  position: 'relative',
                  background: theme.palette.background.paper,
                  border: 'none',
                  borderRadius: 2,
                  animation: `${fadeInUp} 0.6s ease-out ${0.1 + index * 0.05}s both`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    padding: '2px',
                    background: gradient,
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    pointerEvents: 'none',
                  },
                }}
              >
                {/* 大会名 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmojiEventsIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {claim.raceName}
                  </Typography>
                </Box>

                {/* 日時・ステータス・リンク */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(claim.createdAt)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={statusConfig.label} color={statusConfig.color} size="small" sx={{ fontWeight: 600 }} />
                    {claim.txHash && (
                      <Link
                        href={getExplorerUrl(claim.chain, claim.txHash)}
                        target="_blank"
                        rel="noopener"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.75rem',
                        }}
                      >
                        TX
                        <LaunchIcon sx={{ fontSize: 14 }} />
                      </Link>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
